/**
 * @fileoverview Derived employee-status helpers (CJS).
 *
 * Pure functions over a user-like object carrying `accountStatus`,
 * `employmentStatus`, and (for readiness) `email` / `roleId` /
 * `roleName` / `locationIds` / `locationId`. No I/O, no React.
 *
 * @module @puffin-core/status-taxonomy/helpers
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');
const {
  ACCOUNT_STATUS,
  EMPLOYMENT_STATUS,
  DEFAULT_ACCOUNT_STATUS,
  DEFAULT_EMPLOYMENT_STATUS,
  SCHEDULABLE_PREDICATE,
  EMPLOYMENT_STATUS_VALUES,
  ACCOUNT_STATUS_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  ACCOUNT_STATUS_BADGE_TONES,
  SCHEDULE_BLOCKER,
} = require('./constants');

/**
 * Build canonical `[{ value, label }]` filter/select options from a list of
 * status values + a labels map (label falls back to the raw value). The ONE
 * way to turn a status enum into dropdown options — never hand-roll a local
 * label map (enforced by the `web/no-local-status-label-map` ESLint rule).
 *
 * @param {string[]} values
 * @param {Object<string,string>} [labels]
 * @returns {Array<{value:string,label:string}>}
 */
function buildStatusOptions(values, labels = {}) {
  return (values || []).map((value) => ({ value, label: labels[value] || value }));
}

/** Canonical Employment-status dropdown options from the SSOT. */
function buildEmploymentStatusOptions() {
  return buildStatusOptions(EMPLOYMENT_STATUS_VALUES, EMPLOYMENT_STATUS_LABELS);
}

function resolveAccountStatus(user) {
  return (user && user.accountStatus) || DEFAULT_ACCOUNT_STATUS;
}

function resolveEmploymentStatus(user) {
  return (user && user.employmentStatus) || DEFAULT_EMPLOYMENT_STATUS;
}

function hasAssignedRole(user) {
  return Boolean(user && (user.roleId || user.roleName));
}

function hasAssignedLocation(user) {
  if (!user) return false;
  if (Array.isArray(user.locationIds)) return user.locationIds.length > 0;
  return Boolean(user.locationId || user.primaryLocationId);
}

/**
 * The access (login/onboarding) state of a user, with display props.
 * `canLogIn` is true only for `active` (matches the auth-routes gate).
 *
 * `tone` is the badge tone (`<StatusBadgeCell tone={…}>`); an unknown status reads neutral.
 *
 * @returns {{status:string,label:string,tone:string,canLogIn:boolean,
 *   needsOnboarding:boolean,isNotInvited:boolean,isInvited:boolean}}
 */
function deriveAccessState(user) {
  const status = resolveAccountStatus(user);
  return {
    status,
    label: ACCOUNT_STATUS_LABELS[status] || status,
    tone: ACCOUNT_STATUS_BADGE_TONES[status] || BADGE_TONE.NEUTRAL,
    canLogIn: status === ACCOUNT_STATUS.ACTIVE,
    needsOnboarding: status === ACCOUNT_STATUS.IMPORTED || status === ACCOUNT_STATUS.INVITED,
    isNotInvited: status === ACCOUNT_STATUS.IMPORTED,
    isInvited: status === ACCOUNT_STATUS.INVITED,
  };
}

/**
 * Roster (list) visibility gate: anyone not terminated from employment
 * appears on the PlannerPuffin roster (terminated hidden behind a filter).
 */
function isOnRoster(user) {
  return resolveEmploymentStatus(user) !== EMPLOYMENT_STATUS.TERMINATED;
}

/**
 * Hard schedule-placement gate: a person may be assigned a shift only if
 * they can log in (account active) AND are actively employed. On-leave,
 * invited, and not-invited people are visible but not schedulable.
 *
 * Derives from the canonical `SCHEDULABLE_PREDICATE` data so the JS gate and
 * the server-side SQL gates (planner-api `applySchedulableWhere`) share ONE
 * source of truth.
 */
function isSchedulable(user) {
  return (
    resolveAccountStatus(user) === SCHEDULABLE_PREDICATE.accountStatus &&
    resolveEmploymentStatus(user) === SCHEDULABLE_PREDICATE.employmentStatus
  );
}

/**
 * Full schedule-readiness with an ordered blocker list (employment →
 * access → data-completeness). `ready` ⇒ `isSchedulable` AND has a role
 * AND has a location assignment.
 *
 * @returns {{ready:boolean, blockers:string[]}}
 */
function deriveScheduleReadiness(user) {
  const acc = resolveAccountStatus(user);
  const emp = resolveEmploymentStatus(user);
  const blockers = [];

  if (emp === EMPLOYMENT_STATUS.TERMINATED) blockers.push(SCHEDULE_BLOCKER.TERMINATED);
  else if (emp === EMPLOYMENT_STATUS.ON_LEAVE) blockers.push(SCHEDULE_BLOCKER.ON_LEAVE);
  else if (emp === EMPLOYMENT_STATUS.SUSPENDED)
    blockers.push(SCHEDULE_BLOCKER.SUSPENDED_EMPLOYMENT);

  if (acc === ACCOUNT_STATUS.IMPORTED) blockers.push(SCHEDULE_BLOCKER.NOT_INVITED);
  else if (acc === ACCOUNT_STATUS.INVITED) blockers.push(SCHEDULE_BLOCKER.INVITED);
  else if (acc === ACCOUNT_STATUS.INACTIVE) blockers.push(SCHEDULE_BLOCKER.INACTIVE);
  else if (acc === ACCOUNT_STATUS.SUSPENDED) blockers.push(SCHEDULE_BLOCKER.SUSPENDED_ACCOUNT);
  else if (acc === ACCOUNT_STATUS.TERMINATED) blockers.push(SCHEDULE_BLOCKER.ACCOUNT_TERMINATED);

  // An imported profile with no email cannot be invited at all.
  if (acc === ACCOUNT_STATUS.IMPORTED && !(user && user.email)) {
    blockers.push(SCHEDULE_BLOCKER.MISSING_EMAIL);
  }
  if (!hasAssignedRole(user)) blockers.push(SCHEDULE_BLOCKER.NO_ROLE);
  if (!hasAssignedLocation(user)) blockers.push(SCHEDULE_BLOCKER.NO_LOCATION);

  return { ready: blockers.length === 0, blockers };
}

module.exports = {
  deriveAccessState,
  deriveScheduleReadiness,
  isSchedulable,
  isOnRoster,
  buildStatusOptions,
  buildEmploymentStatusOptions,
};
